/**
 * User Preferences Store
 *
 * Zustand store for managing user preferences state and updates.
 * Provides centralized state management for user preferences across the application.
 *
 * This store integrates with AuthStore to ensure preferences are synced:
 * - When user logs in, preferences are initialized from user.preferences
 * - When preferences are updated, both stores are updated to keep them in sync
 */

import { persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';

import { createPersistStorage } from './persist-storage';
import { registerSessionScoped } from './session-scope';
import {
  registerPreferencesStoreCallbacks,
  registerMeasurementSystemAccessor,
  syncUpdateAuthUserPreferences,
  syncGetAuthUserPreferences,
} from './store-sync';
import { InvalidDataError } from '../errors/invalid-data.error';
import { userPreferencesService } from '../services/user-preferences.service';
import {
  API_ERROR_CODES,
  ApiErrorClass,
  isRetryableError,
  isSessionEndedError,
} from '../types/api/errors';
import { DEFAULT_USER_PREFERENCES } from '../types/user-preferences.types';
import { dayjs } from '../utils/date';

import type { UserPreferencesErrorCode } from '../types/domain/user-preferences.types';
import type { UpdateUserPreferencesRequest } from '../types/user-preferences.types';
import type { UserPreferences } from '../types/user-preferences.types';

// =============================================================================
// STORE INTERFACES
// =============================================================================

export interface UserPreferencesStoreState {
  // Preferences data
  preferences: UserPreferences;

  // Loading states
  isLoading: boolean;
  isUpdating: boolean;

  // Error states
  error: string | null;
  updateError: string | null;
  errorCode: UserPreferencesErrorCode | null;
  isRetryable: boolean;
  validationErrors: Array<{ field: string; message: string }>;

  // Last sync tracking
  lastUpdatedAt: number | null;
}

export interface UserPreferencesStoreActions {
  // Core preference actions
  updatePreferences: (updates: UpdateUserPreferencesRequest) => Promise<void>;
  setPreferences: (preferences: UserPreferences) => void;

  // Auth store sync actions
  syncWithAuthStore: () => void;
  initializeFromUser: (preferences: UserPreferences | null | undefined) => void;

  // Individual field updates
  setTheme: (theme: UserPreferences['theme_selection']) => void;
  setMeasurementSystem: (system: UserPreferences['measurement_system']) => void;
  setLanguage: (language: UserPreferences['language']) => void;
  setDashboardProfile: (profile: UserPreferences['dashboard_profile']) => void;
  setWorkoutsPerWeek: (count: number | null) => void;
  setPreferredWorkoutDays: (
    days: UserPreferences['preferred_workout_days']
  ) => void;
  setNotificationPreferences: (
    prefs: UserPreferences['notification_preferences']
  ) => void;
  setPrivacySettings: (settings: UserPreferences['privacy_settings']) => void;

  // State management
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  clearValidationErrors: () => void;

  // Store reset
  reset: () => void;
}

export type UserPreferencesStore = UserPreferencesStoreState &
  UserPreferencesStoreActions;

// =============================================================================
// STORE CONFIGURATION
// =============================================================================

const STORAGE_KEY = 'personal-trainer-user-preferences';

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: UserPreferencesStoreState = {
  preferences: DEFAULT_USER_PREFERENCES,
  isLoading: false,
  isUpdating: false,
  error: null,
  updateError: null,
  errorCode: null,
  isRetryable: false,
  validationErrors: [],
  lastUpdatedAt: null,
};

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

export const userPreferencesStore = createStore<UserPreferencesStore>()(
  persist(
    (set, get) => ({
      // =======================================================================
      // INITIAL STATE
      // =======================================================================
      ...initialState,

      // =======================================================================
      // CORE PREFERENCE ACTIONS
      // =======================================================================

      updatePreferences: async (
        updates: UpdateUserPreferencesRequest
      ): Promise<void> => {
        const {
          setLoading,
          setError,
          clearError,
          clearValidationErrors,
          preferences,
        } = get();

        setLoading(true);
        set({
          isUpdating: true,
          updateError: null,
          errorCode: null,
          isRetryable: false,
        });
        clearError();
        clearValidationErrors();

        try {
          await userPreferencesService.updatePreferences(updates);

          // Merge updates with current preferences
          const updatedPreferences: UserPreferences = {
            ...preferences,
            ...updates,
          };

          // Update local state after successful API call
          set({
            preferences: updatedPreferences,
            lastUpdatedAt: dayjs().valueOf(),
          });

          // Sync with AuthStore to update user.preferences
          syncUpdateAuthUserPreferences(updatedPreferences);
        } catch (error) {
          // The session that asked is over; there is nobody to show this to.
          // (The one store catch that maps more than a message, so it does
          // not go through `failureMessage`.)
          if (isSessionEndedError(error)) return;

          let errorMessage = 'Failed to update preferences';
          let errorCode: UserPreferencesErrorCode =
            API_ERROR_CODES.UNKNOWN_ERROR;
          let isRetryable = false;

          if (error instanceof InvalidDataError) {
            errorMessage = error.message;
            errorCode = API_ERROR_CODES.VALIDATION_ERROR;

            // Extract error code from details if available
            const details = error.details as
              | {
                  errorCode?: UserPreferencesErrorCode;
                  isRetryable?: boolean;
                }
              | undefined;
            if (details?.errorCode) {
              errorCode = details.errorCode;
            }
            if (details?.isRetryable) {
              isRetryable = details.isRetryable;
            }

            // Extract validation errors if available
            const validationErrors = error.validationErrors.map(
              (validationError) => ({
                field: validationError.field,
                message: validationError.message,
              })
            );

            set({ validationErrors });
          } else if (error instanceof ApiErrorClass) {
            // The service now re-throws the coordinator's typed error by
            // identity (de-clobbering), so pull the code and retryability off
            // the typed shape (`.code` / `.retryable`) rather than the old
            // hand-attached `.isRetryable` flag.
            errorMessage = error.message;
            errorCode = error.code as UserPreferencesErrorCode;
            isRetryable = isRetryableError(error);
          } else if (error instanceof Error) {
            errorMessage = error.message;

            // Extract error code and retryable flag if available on enhanced error
            const enhancedError = error as Error & {
              code?: UserPreferencesErrorCode;
              isRetryable?: boolean;
            };
            if (enhancedError.code) {
              errorCode = enhancedError.code;
            }
            if (enhancedError.isRetryable !== undefined) {
              isRetryable = enhancedError.isRetryable;
            }
          }

          setError(errorMessage);
          set({ updateError: errorMessage, errorCode, isRetryable });
          throw error;
        } finally {
          setLoading(false);
          set({ isUpdating: false });
        }
      },

      setPreferences: (preferences: UserPreferences): void => {
        set({
          preferences,
          lastUpdatedAt: dayjs().valueOf(),
        });
      },

      // =======================================================================
      // AUTH STORE SYNC ACTIONS
      // =======================================================================

      /**
       * Synchronizes the user preferences store with the current AuthStore user.
       * Call this when the user logs in or when you need to refresh preferences from auth state.
       */
      syncWithAuthStore: (): void => {
        // Use store-sync utility to avoid circular dependencies
        const preferences = syncGetAuthUserPreferences();
        if (preferences) {
          set({
            preferences,
            lastUpdatedAt: dayjs().valueOf(),
          });
        }
      },

      /**
       * Initializes preferences from a user object (typically from login/register response).
       * Use this to set preferences when user data is available.
       */
      initializeFromUser: (
        preferences: UserPreferences | null | undefined
      ): void => {
        if (preferences) {
          set({
            preferences,
            lastUpdatedAt: dayjs().valueOf(),
            error: null,
            updateError: null,
            validationErrors: [],
          });
        }
      },

      // =======================================================================
      // INDIVIDUAL FIELD UPDATES
      // =======================================================================

      setTheme: (theme: UserPreferences['theme_selection']): void => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            theme_selection: theme,
          },
        }));
      },

      setMeasurementSystem: (
        system: UserPreferences['measurement_system']
      ): void => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            measurement_system: system,
          },
        }));
      },

      setLanguage: (language: UserPreferences['language']): void => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            language,
          },
        }));
      },

      setDashboardProfile: (
        profile: UserPreferences['dashboard_profile']
      ): void => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            dashboard_profile: profile,
          },
        }));
      },

      setWorkoutsPerWeek: (count: number | null): void => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            workouts_per_week: count,
          },
        }));
      },

      setPreferredWorkoutDays: (
        days: UserPreferences['preferred_workout_days']
      ): void => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            preferred_workout_days: days,
          },
        }));
      },

      setNotificationPreferences: (
        prefs: UserPreferences['notification_preferences']
      ): void => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            notification_preferences: prefs,
          },
        }));
      },

      setPrivacySettings: (
        settings: UserPreferences['privacy_settings']
      ): void => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            privacy_settings: settings,
          },
        }));
      },

      // =======================================================================
      // STATE MANAGEMENT
      // =======================================================================

      setLoading: (loading: boolean): void => {
        set({ isLoading: loading });
      },

      setError: (error: string | null): void => {
        set({ error });
      },

      clearError: (): void => {
        set({
          error: null,
          updateError: null,
          errorCode: null,
          isRetryable: false,
        });
      },

      clearValidationErrors: (): void => {
        set({ validationErrors: [] });
      },

      // =======================================================================
      // STORE RESET
      // =======================================================================

      reset: (): void => {
        set(initialState);
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createPersistStorage(),
      partialize: (state): Partial<UserPreferencesStoreState> => ({
        // Only persist preferences data
        preferences: state.preferences,
        lastUpdatedAt: state.lastUpdatedAt,
      }),
      version: 1,
    }
  )
);

// =============================================================================
// STORE SELECTORS
// =============================================================================

/**
 * Selector for all preferences
 */
export const selectPreferences = (
  state: UserPreferencesStore
): UserPreferences => state.preferences;

/**
 * Selector for theme preference
 */
export const selectTheme = (
  state: UserPreferencesStore
): UserPreferences['theme_selection'] => state.preferences.theme_selection;

/**
 * Selector for measurement system preference
 */
export const selectMeasurementSystem = (
  state: UserPreferencesStore
): UserPreferences['measurement_system'] =>
  state.preferences.measurement_system;

/**
 * Selector for language preference
 */
export const selectLanguage = (
  state: UserPreferencesStore
): UserPreferences['language'] => state.preferences.language;

/**
 * Selector for dashboard profile preference
 */
export const selectDashboardProfile = (
  state: UserPreferencesStore
): UserPreferences['dashboard_profile'] => state.preferences.dashboard_profile;

/**
 * Selector for workouts per week
 */
export const selectWorkoutsPerWeek = (
  state: UserPreferencesStore
): number | null => state.preferences.workouts_per_week;

/**
 * Selector for preferred workout days
 */
export const selectPreferredWorkoutDays = (
  state: UserPreferencesStore
): UserPreferences['preferred_workout_days'] =>
  state.preferences.preferred_workout_days;

/**
 * Selector for notification preferences
 */
export const selectNotificationPreferences = (
  state: UserPreferencesStore
): UserPreferences['notification_preferences'] =>
  state.preferences.notification_preferences;

/**
 * Selector for privacy settings
 */
export const selectPrivacySettings = (
  state: UserPreferencesStore
): UserPreferences['privacy_settings'] => state.preferences.privacy_settings;

/**
 * Selector for loading states
 */
export const selectPreferencesLoading = (
  state: UserPreferencesStore
): {
  isLoading: boolean;
  isUpdating: boolean;
} => ({
  isLoading: state.isLoading,
  isUpdating: state.isUpdating,
});

/**
 * Selector for error states
 */
export const selectPreferencesErrors = (
  state: UserPreferencesStore
): {
  error: string | null;
  updateError: string | null;
  errorCode: UserPreferencesErrorCode | null;
  isRetryable: boolean;
  validationErrors: Array<{ field: string; message: string }>;
} => ({
  error: state.error,
  updateError: state.updateError,
  errorCode: state.errorCode,
  isRetryable: state.isRetryable,
  validationErrors: state.validationErrors,
});

/**
 * Selector for error code
 */
export const selectErrorCode = (
  state: UserPreferencesStore
): UserPreferencesErrorCode | null => state.errorCode;

/**
 * Selector for retryable state
 */
export const selectIsRetryable = (state: UserPreferencesStore): boolean =>
  state.isRetryable;

/**
 * Selector for last update timestamp
 */
export const selectLastUpdatedAt = (
  state: UserPreferencesStore
): number | null => state.lastUpdatedAt;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get current preferences
 */
export const getPreferences = (): UserPreferences => {
  return userPreferencesStore.getState().preferences;
};

/**
 * Get current theme
 */
export const getTheme = (): UserPreferences['theme_selection'] => {
  return userPreferencesStore.getState().preferences.theme_selection;
};

/**
 * Get current measurement system
 */
export const getMeasurementSystem =
  (): UserPreferences['measurement_system'] => {
    return userPreferencesStore.getState().preferences.measurement_system;
  };

/**
 * Get current language
 */
export const getLanguage = (): UserPreferences['language'] => {
  return userPreferencesStore.getState().preferences.language;
};

/**
 * Check if store is currently updating preferences
 */
export const isUpdatingPreferences = (): boolean => {
  return userPreferencesStore.getState().isUpdating;
};

/**
 * Check if store has any errors
 */
export const hasError = (): boolean => {
  const state = userPreferencesStore.getState();
  return state.error !== null || state.updateError !== null;
};

/**
 * Get the current error code
 */
export const getErrorCode = (): UserPreferencesErrorCode | null => {
  return userPreferencesStore.getState().errorCode;
};

/**
 * Check if the current error is retryable
 */
export const isErrorRetryable = (): boolean => {
  return userPreferencesStore.getState().isRetryable;
};

/**
 * Check if the current error is an authentication error
 * Useful for UI to show login prompt
 */
export const isAuthenticationError = (): boolean => {
  const errorCode = userPreferencesStore.getState().errorCode;
  return (
    errorCode === API_ERROR_CODES.UNAUTHENTICATED ||
    errorCode === API_ERROR_CODES.FORBIDDEN
  );
};

// =============================================================================
// AUTH STORE SYNC HELPERS
// =============================================================================

/**
 * Sync preferences from AuthStore user object.
 * Call this after login/register to initialize preferences.
 */
export const syncPreferencesWithAuthStore = (): void => {
  userPreferencesStore.getState().syncWithAuthStore();
};

/**
 * Initialize preferences from user data.
 * Call this with user.preferences after successful authentication.
 */
export const initializePreferencesFromUser = (
  preferences: UserPreferences | null | undefined
): void => {
  userPreferencesStore.getState().initializeFromUser(preferences);
};

/**
 * Reset preferences store (e.g., on logout).
 * Clears all preferences data and resets to defaults.
 */
export const resetPreferencesStore = (): void => {
  userPreferencesStore.getState().reset();
};

// =============================================================================
// STORE SYNC REGISTRATION
// =============================================================================

// Register preferences store callbacks for cross-store synchronization
registerPreferencesStoreCallbacks({
  initializePreferences: (preferences) =>
    userPreferencesStore.getState().initializeFromUser(preferences),
  resetPreferences: () => userPreferencesStore.getState().reset(),
});

// Preferences are the account's, and `initializeFromUser` fills them in again
// on login. Resetting here, and not on each sign-out path, means the paths
// that used to skip it (web's 401, mobile's discard) reset them too.
registerSessionScoped('userPreferencesStore', () => {
  userPreferencesStore.getState().reset();
});

// Expose the current measurement system to the service layer (CrudService) so
// unit-bearing caches can vary their keys by unit without importing this store.
registerMeasurementSystemAccessor(
  () => userPreferencesStore.getState().preferences.measurement_system
);
